import { readAtom, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { user$ } from ".";
import { authSessionInit, cleanupSession } from "./auth";
import { crossIdentityBoundary } from "./identity-boundary";
import { type StoredSessionChange, getValidOidcUser, subscribeToStoredSessionChanges, tokenFromOidcUser } from "./oidc";
import { accessToken$, impersonateEmail$, mfaEnrollmentToken$ } from "../session/atom";

/**
 * The callback route completes the code exchange and hydrates itself (see `AuthCallback`), the
 * same way `App`'s autologin stands aside for it. A reconcile racing that would clear the query
 * cache out from under the hydration already in flight.
 */
function isCompletingSignin(): boolean {
	return window.location.pathname.startsWith("/auth/callback");
}

/**
 * Bring this tab in line with the OIDC session the browser now holds, after another tab changed
 * it. The stored `User` is re-read rather than trusted from the event: `change` says what
 * happened, the store says what is true — and with more than one pool configured, a sign-out on
 * one of them is not a sign-out at all.
 */
async function reconcile(change: StoredSessionChange): Promise<void> {
	if (isCompletingSignin()) {
		return;
	}

	const token = tokenFromOidcUser(await getValidOidcUser());
	const wasSignedIn = readAtom(user$).signedIn;

	if (!token) {
		if (!wasSignedIn) {
			return;
		}
		// Signed out elsewhere. Settle locally and let the route guards do the rest: the tab that
		// actually signed out is already on its way through the Hosted UI `/logout`, and calling
		// `logout` here would only race it to the same place — through a second `removeUser` that
		// can just as easily land on a *newer* session than the one being ended.
		writeAtom(accessToken$, null);
		writeAtom(mfaEnrollmentToken$, null);
		writeAtom(impersonateEmail$, null);
		cleanupSession();
		return;
	}

	if (wasSignedIn && change.kind === "renewed" && readAtom(mfaEnrollmentToken$) == null) {
		// A silent renew in another tab: same subject, fresh credential, an identity this tab has
		// already resolved. Adopt the token and leave that identity — and every query keyed on
		// it — untouched; re-running hydration here would refetch the world on every renew, in
		// every open tab. A tab still behind the MFA gate is excluded: `accessToken$` is held
		// empty there on purpose, and only `authSessionInit` may judge that the gate has cleared.
		writeAtom(accessToken$, token);
		return;
	}

	// Signed in (or signed in as somebody else) elsewhere: resolve the identity here, from this
	// tab's own `/user/info`, exactly as an interactive sign-in would. `authSessionInit` drops
	// the per-user state at rest when the id it resolves is not the one this browser last held.
	//
	// Across the boundary, for the same reason an impersonation is: nobody touched this tab, so every
	// page it had is still mounted, and a grid that re-renders while `authSessionInit` swaps the store
	// under it persists the outgoing user's layout as the arriving one. See `./identity-boundary.ts`.
	await crossIdentityBoundary(() => authSessionInit(token));
}

/**
 * Keep every tab of this browser on the same session.
 *
 * Tabs share one credential store and nothing else — the bearer token is per-tab and in memory
 * (MDOT-24702), and `user$` is deliberately not mirrored across tabs. Without this, a session
 * change reached only the tab that made it: signing in on one tab left the others sitting on
 * `/login` with a perfectly good session in storage, and signing out left them rendering a shell
 * they had no token for until something 401'd.
 *
 * Reconciles are serialised. Two changes can land back to back — `completeSignin` clears the
 * other pools before it stores its own user — and each one is settled before the next is read,
 * so a stale `signed-out` can never overwrite the sign-in that followed it.
 *
 * Returns the unsubscribe.
 */
export function installCrossTabSessionSync(): () => void {
	let pending: Promise<void> = Promise.resolve();
	return subscribeToStoredSessionChanges((change) => {
		pending = pending
			.then(() => reconcile(change))
			.catch((err) => {
				// A failed reconcile leaves this tab as it was: signed out stays signed out (the
				// user can sign in here as usual), and a live session keeps its token. Nothing to
				// escalate — the next 401 goes through the renew path like any other.
				console.warn("Cross-tab session reconcile failed", err);
			});
	});
}
